import React from "react";
import type { SearchResults, NormalizedProduct, SourceId } from "../../types";
import { ALL_SOURCES, SOURCE_LABELS } from "../../types";
import { SourceBadge } from "./SourceBadge";

interface Props {
  results: SearchResults;
  onPick: (product: NormalizedProduct) => void;
  onBack: () => void;
}

function timeAgo(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "agora";
  if (mins < 60) return `${mins}min atras`;
  const hours = Math.floor(mins / 60);
  return `${hours}h atras`;
}

function formatPrice(price: number): string {
  return `R$ ${price.toFixed(2)}`;
}

export function ProductPicker({ results, onPick, onBack }: Props) {
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Resultados para "{results.term}"
        </h2>
        <button
          onClick={onBack}
          className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          Nova busca
        </button>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        Selecione o produto principal para comparar:
      </p>

      {ALL_SOURCES.map((source) => {
        const sr = results.results[source];
        return (
          <div key={source} className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <SourceBadge source={source} />
              <span className="text-xs text-gray-400 dark:text-gray-500">
                atualizado {timeAgo(sr.fetchedAt)}
              </span>
              {sr.error && (
                <span className="text-xs text-red-500">{sr.error}</span>
              )}
            </div>

            {sr.products.length === 0 && !sr.error && (
              <p className="text-sm text-gray-400 dark:text-gray-500 ml-2">
                Nenhum resultado
              </p>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {sr.products.slice(0, 6).sort((a, b) => a.price - b.price).map((product) => (
                <button
                  key={product.id}
                  onClick={() => onPick(product)}
                  className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-sm transition-all text-left"
                >
                  {product.imageUrl && (
                    <img
                      src={product.imageUrl}
                      alt=""
                      className="w-12 h-12 object-contain rounded flex-shrink-0"
                      loading="lazy"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {product.name}
                    </p>
                    {product.merchantName !==
                      SOURCE_LABELS[product.source] && (
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        via {product.merchantName}
                      </p>
                    )}
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-bold text-green-700 dark:text-green-400">
                        {formatPrice(product.price)}
                      </span>
                      {product.listPrice > product.price && (
                        <span className="text-xs text-gray-400 dark:text-gray-500 line-through">
                          {formatPrice(product.listPrice)}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
